/**
 * Feb-2026 Iter 28 — KI-Plausibilitäts-Check Banner.
 *
 * Sitzt in der Pricing-Sektion direkt UNTER dem Quality-Score-Card und zeigt
 * KI-erkannte Widersprüche/Tippfehler im Inserat. Debounced 1.500 ms nach
 * jeder Form-Änderung; LLM-Call ist async + Mock-Fallback bei fehlendem Key.
 *
 * Endpoint: POST /api/listings/ai-plausibility-check
 * Antwort:  { warnings: [{severity, field, message, suggestion}], model, mocked }
 */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, AlertCircle, Info, X, ShieldCheck, Clock, UserPlus, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import AssignTaskDialog from "../dashboard/AssignTaskDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";

const SEVERITY_STYLES = {
    high:   { color: "#DC2626", bg: "bg-red-50",    border: "border-red-300",    Icon: AlertCircle,    label: "Kritisch" },
    medium: { color: "#D97706", bg: "bg-amber-50",  border: "border-amber-300",  Icon: AlertTriangle,  label: "Hinweis" },
    low:    { color: "#6B7280", bg: "bg-gray-50",   border: "border-gray-300",   Icon: Info,           label: "Info" },
};

export default function PlausibilityCheckBanner({ form, refreshSignal = 0 }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dismissed, setDismissed] = useState({}); // severity+field+message → true
    // Jul-2026 (User-Wunsch) — persistierte Hinweis-Aktionen: Ignorieren (dauerhaft)
    // + Schlummern (temporär). Seed aus dem geladenen Inserat.
    const [ignoredFields, setIgnoredFields] = useState(() => new Set(form?.quality_hint_ignores || []));
    const [snoozedFields, setSnoozedFields] = useState(() => {
        const s = new Set();
        const now = Date.now();
        Object.entries(form?.quality_hint_snoozes || {}).forEach(([f, until]) => {
            if (until && new Date(until).getTime() > now) s.add(f);
        });
        return s;
    });
    const [assignItem, setAssignItem] = useState(null);

    // Ignorieren dürfen nur Filialleiter / Disposition / Verkaufsleiter (bzw. Inhaber/Admin).
    const empFn = (user?.employee_function || "").toLowerCase();
    const canIgnore = !!user && (user.role === "admin" || user.role === "b2b" || ["filialleiter", "disposition", "verkaufsleiter"].includes(empFn));

    const persistAction = async (field, action, extra = {}) => {
        if (!form?.id) return true; // vor dem ersten Speichern nur lokal
        try {
            await api.post(`/listings/${form.id}/quality-hint-action`, { field, action, ...extra });
            return true;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen");
            return false;
        }
    };

    const handleSnooze = async (field, days, label) => {
        const ok = await persistAction(field, "snooze", { days });
        if (ok) { setSnoozedFields((s) => new Set(s).add(field)); toast.success(`Hinweis für ${label} geschlummert.`); }
    };
    const handleIgnore = async (field) => {
        if (!canIgnore) { toast.error("Ignorieren dürfen nur Filialleiter, Disposition oder Verkaufsleiter."); return; }
        const ok = await persistAction(field, "ignore");
        if (ok) { setIgnoredFields((s) => new Set(s).add(field)); toast.success("Hinweis dauerhaft ignoriert."); }
    };
    const handleAssign = (w) => {
        setAssignItem({ listing_id: form?.id, title: `Hinweis beheben: ${w.message}`, body: w.suggestion || "", type: "quality_hint", field: w.field });
    };
    // Feb-2026 (User-Wunsch 25 May, Update v13): Instant-Plausibilitäts-Check.
    // Sobald der Nutzer ein Feld korrigiert, das aktuell eine Warnung trägt,
    // verstecken wir diese Warnung SOFORT (ohne 1.5 s zu warten). Nach dem
    // nächsten API-Call wird die Warnliste wieder anhand des frischen Server-
    // Ergebnisses gerendert.
    const [staleFields, setStaleFields] = useState(() => new Set());
    const prevFormRef = useRef(form);
    const timer = useRef(null);

    // Track geänderte Felder seit letzter API-Antwort
    useEffect(() => {
        const prev = prevFormRef.current || {};
        const dirty = new Set(staleFields);
        let changed = false;
        Object.keys(form).forEach((k) => {
            if (prev[k] !== form[k]) {
                dirty.add(k);
                changed = true;
            }
        });
        prevFormRef.current = form;
        if (changed) setStaleFields(dirty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form]);

    useEffect(() => {
        // Erst rechnen, wenn die wichtigsten Pflichtfelder gesetzt sind
        if (!form.brand || !form.model) {
            setData(null);
            return undefined;
        }
        if (timer.current) clearTimeout(timer.current);
        // Feb-2026 Iter 28 — Bei manuell ausgelöstem Refresh (refreshSignal > 0)
        // ohne Debounce sofort feuern.
        const delay = refreshSignal > 0 ? 50 : 1500;
        timer.current = setTimeout(async () => {
            setLoading(true);
            try {
                const { data: resp } = await api.post("/listings/ai-plausibility-check", form);
                setData(resp);
                // Frische Antwort → alle stale-Markierungen löschen.
                setStaleFields(new Set());
            } catch {
                // silent — Plausibilitäts-Check ist optional UI
            } finally {
                setLoading(false);
            }
        }, delay);
        return () => timer.current && clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, refreshSignal]);

    if (!data) return null;
    const visible = (data.warnings || []).filter((w) => {
        if (dismissed[`${w.severity}-${w.field}-${w.message}`]) return false;
        if (w.field && ignoredFields.has(w.field)) return false;
        if (w.field && snoozedFields.has(w.field)) return false;
        // Instant-Hide: Feld wurde nach letztem API-Call editiert → Warnung
        // sofort ausblenden, bis der nächste Check die Lage bestätigt.
        if (w.field && staleFields.has(w.field)) return false;
        return true;
    });

    // Feb-2026 Iter 28 (User-Wunsch 25 May, Update v12): Klick auf eine Warnung
    // springt zum entsprechenden Formularfeld. Re-Use Pattern aus
    // QualityScoreCard.jumpTo: testid finden, eingeklappte Section aufklappen,
    // smooth scroll + Highlight-Ring.
    const FIELD_TO_TESTID = {
        price: "form-price",
        price_b2c: "form-price",
        price_b2b: "form-price-b2b",
        tuev_until: "form-tuev-until-month",
        mileage: "form-mileage",
        title: "form-title",
        description: "form-description",
        fuel: "form-fuel",
        power_kw: "form-power-kw",
        power_hp: "form-power-hp",
        displacement: "form-displacement",
        brand: "form-brand",
        model: "form-model",
        first_registration_date: "form-erstzulassung",
        previous_owners: "extra-previous-owners",
        color_base: "form-color-base",
        condition: "listing-condition",
        vin: "form-vin",
        hsn: "form-hsn",
        tsn: "form-tsn",
        license_plate: "form-license-plate",
        last_service_date: "form-last-service-date-month",
        weight_kg: "form-weight",
        max_weight_kg: "form-max-weight",
        length_mm: "form-length",
        width_mm: "form-width",
        height_mm: "form-height",
        tire_size_front: "form-tire-front",
        tire_size_rear: "form-tire-rear",
        seat_height_mm: "form-seat-height",
        tank_capacity_l: "form-tank-capacity",
        emission_class: "form-emission-class",
        vehicle_class: "form-vehicle-class",
    };
    const SECTION_FALLBACK = {
        "form-description": "service-section",
        "form-vin": "disposition-section-toggle",
        "form-tuev-until-month": "service-section",
        "form-last-service-date-month": "service-section",
    };

    const jumpToField = (fieldName) => {
        if (!fieldName) return;
        const testid = FIELD_TO_TESTID[fieldName] || `form-${String(fieldName).replace(/_/g, "-")}`;
        const highlight = (el) => {
            try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ignore */ }
            el.classList.add("ring-2", "ring-[#FFCC00]", "ring-offset-2");
            setTimeout(() => el.classList.remove("ring-2", "ring-[#FFCC00]", "ring-offset-2"), 1800);
            if (typeof el.focus === "function") {
                try { el.focus({ preventScroll: true }); } catch { /* ignore */ }
            }
        };
        const expandAncestors = (target) => {
            let node = target.parentElement;
            const seen = new Set();
            while (node && !seen.has(node)) {
                seen.add(node);
                const btn = node.querySelector?.(':scope > div > [aria-expanded="false"], :scope > [aria-expanded="false"]');
                if (btn) {
                    try { btn.click(); } catch { /* ignore */ }
                }
                node = node.parentElement;
            }
        };
        const tryFind = () => {
            const el = document.querySelector(`[data-testid="${testid}"]`);
            if (el) {
                expandAncestors(el);
                setTimeout(() => highlight(el), 180);
                return true;
            }
            const sectionTestid = SECTION_FALLBACK[testid];
            if (sectionTestid) {
                const sec = document.querySelector(`[data-testid="${sectionTestid}"]`);
                if (sec) {
                    const toggle = sec.querySelector('[aria-expanded="false"]');
                    if (toggle) {
                        try { toggle.click(); } catch { /* ignore */ }
                    }
                    setTimeout(() => {
                        const found = document.querySelector(`[data-testid="${testid}"]`);
                        if (found) highlight(found);
                        else highlight(sec);
                    }, 280);
                    return true;
                }
            }
            return false;
        };
        tryFind();
    };
    if (visible.length === 0) {
        // Wenn die KI vorher Warnungen hatte und der Nutzer alles aufgelöst hat,
        // belohnen wir mit einem grünen All-Clear (max. 4 s sichtbar).
        if ((data.warnings || []).length === 0 && form.brand && form.model) {
            return (
                <div
                    className="mb-3 border-2 border-emerald-300 bg-emerald-50 rounded-sm p-3 flex items-center gap-2"
                    data-testid="plausi-banner-allclear"
                >
                    <ShieldCheck className="w-4 h-4 text-emerald-700" />
                    <span className="text-[12px] font-bold uppercase tracking-wider text-emerald-800">
                        Plausibilitäts-Check: alle Angaben stimmig
                    </span>
                    {loading && <span className="text-[10px] text-emerald-700 italic ml-1">prüfe…</span>}
                </div>
            );
        }
        return null;
    }

    return (
        <div className="mb-3 space-y-1.5" data-testid="plausi-banner">
            {visible.slice(0, 5).map((w, idx) => {
                const style = SEVERITY_STYLES[w.severity] || SEVERITY_STYLES.low;
                const key = `${w.severity}-${w.field}-${w.message}`;
                const Icon = style.Icon;
                return (
                    <div
                        key={`${idx}-${key}`}
                        className={`border-2 ${style.border} ${style.bg} rounded-sm p-2.5 flex items-start gap-2 hover:shadow-md cursor-pointer transition-shadow group`}
                        data-testid={`plausi-warning-${idx}`}
                        onClick={() => jumpToField(w.field)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                jumpToField(w.field);
                            }
                        }}
                        title={`Zum Feld „${w.field}" springen`}
                    >
                        <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: style.color }} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 flex-wrap">
                                <span
                                    className="text-[9px] font-bold uppercase tracking-widest font-mono-num px-1.5 py-0.5 rounded-sm text-white"
                                    style={{ backgroundColor: style.color }}
                                >
                                    {style.label}
                                </span>
                                <span className="text-[11px] font-mono uppercase tracking-wider text-gray-500 group-hover:text-gray-900 group-hover:underline">
                                    {w.field || "—"}
                                </span>
                                {data.mocked && (
                                    <span className="text-[9px] font-mono uppercase tracking-wider text-gray-400">
                                        Heuristik
                                    </span>
                                )}
                            </div>
                            <p className="text-[13px] text-gray-900 mt-0.5 leading-snug">{w.message}</p>
                            {w.suggestion && (
                                <p className="text-[11.5px] text-gray-600 mt-0.5 italic leading-snug">→ {w.suggestion}</p>
                            )}
                            {/* Jul-2026 (User-Wunsch) — Aktionen: Schlummern · Zuweisen · Ignorieren */}
                            <div className="flex items-center gap-1.5 flex-wrap mt-1.5" onClick={(e) => e.stopPropagation()}>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button type="button"
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-gray-300 bg-white text-gray-700 hover:border-[#111827]"
                                            data-testid={`plausi-snooze-${idx}`}>
                                            <Clock className="w-3 h-3" /> Schlummern
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start" className="min-w-[8rem]">
                                        <DropdownMenuItem onClick={() => handleSnooze(w.field, 1, "1 Tag")} data-testid={`plausi-snooze-1d-${idx}`}>1 Tag</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleSnooze(w.field, 3, "3 Tage")} data-testid={`plausi-snooze-3d-${idx}`}>3 Tage</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleSnooze(w.field, 7, "7 Tage")} data-testid={`plausi-snooze-7d-${idx}`}>7 Tage</DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <button type="button" onClick={() => handleAssign(w)}
                                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-gray-300 bg-white text-gray-700 hover:border-[#111827]"
                                    data-testid={`plausi-assign-${idx}`}>
                                    <UserPlus className="w-3 h-3" /> Zuweisen
                                </button>
                                {canIgnore && (
                                    <button type="button" onClick={() => handleIgnore(w.field)}
                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                                        data-testid={`plausi-ignore-${idx}`}>
                                        <EyeOff className="w-3 h-3" /> Ignorieren
                                    </button>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDismissed((d) => ({ ...d, [key]: true }));
                            }}
                            className="shrink-0 text-gray-400 hover:text-gray-700"
                            data-testid={`plausi-warning-dismiss-${idx}`}
                            aria-label="Warnung ausblenden"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                );
            })}
            {visible.length > 5 && (
                <div className="text-[10px] text-gray-500 italic px-1">
                    + {visible.length - 5} weitere Hinweise (priorisiert nach Schwere)
                </div>
            )}
            <AssignTaskDialog
                open={!!assignItem}
                item={assignItem}
                onClose={() => setAssignItem(null)}
                onAssigned={() => {
                    if (assignItem?.field) setSnoozedFields((s) => new Set(s).add(assignItem.field));
                    setAssignItem(null);
                    toast.success("Hinweis einem Mitarbeiter zugewiesen.");
                }}
            />
        </div>
    );
}
